import React, { useEffect, useState } from 'react';
import {
  Modal, Form, Input, Select, DatePicker, Row, Col, Button, Space, Divider, Checkbox,
} from 'antd';
import { CarOutlined, UserOutlined, FileTextOutlined, GlobalOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { emettre } from '../../services/attestationDigitaleService';
import { listerOrganismesCompagnies } from '../../services/attestationsService';
import { PAYS_CEDEAO } from '../../variables/paysCedeao';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';

const { Option } = Select;

export default function EmettreAttestationModal({ open, onClose, onEmis }) {
  const { t } = useTranslation('attestationsDigitales');
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [compagnies, setCompagnies] = useState([]);

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue({
        typeAttestation: 'DIGITALE',
        paysEmetteurCode: 'TG',
        paysCouverts: PAYS_CEDEAO.map((p) => p.code2),
        canauxEnvoi: ['EMAIL'],
      });
      listerOrganismesCompagnies()
        .then(setCompagnies)
        .catch(() => toast.error(i18n.t('attestationsDigitales:emettre.erreurCompagnies')));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const payload = {
        numeroPolice: values.numeroPolice,
        contratRefExterne: values.compagnieId,
        encaissementRefExterne: values.encaissementRefExterne,
        typeAttestation: values.typeAttestation,
        dateEffet: values.dateEffet ? values.dateEffet.format('YYYY-MM-DD') : undefined,
        dateEcheance: values.dateEcheance ? values.dateEcheance.format('YYYY-MM-DD') : undefined,
        paysEmetteurCode: values.paysEmetteurCode,
        paysCouverts: values.paysCouverts,
        souscripteur: {
          nom: values.souscripteurNom,
          prenom: values.souscripteurPrenom || undefined,
          telephone: values.souscripteurTelephone || undefined,
          email: values.souscripteurEmail || undefined,
        },
        vehicule: {
          immatriculation: values.vehiculeImmatriculation,
          marque: values.vehiculeMarque || undefined,
          modele: values.vehiculeModele || undefined,
          chassis: values.vehiculeChassis || undefined,
          genre: values.vehiculeGenre || undefined,
        },
        canauxEnvoi: values.canauxEnvoi || [],
      };

      const result = await emettre(payload);
      toast.success(t('emettre.succes', { numero: result?.numeroAttestation || '' }));
      onEmis(result);
    } catch (e) {
      if (e?.errorFields) return;
      const msg = e?.response?.data?.message || e?.response?.data?.errors?.join(' - ') || t('emettre.erreur');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const f = { marginBottom: 10 };

  return (
    <Modal
      title={t('emettre.titre')}
      open={open}
      onCancel={onClose}
      width={760}
      destroyOnClose
      footer={
        <Space>
          <Button onClick={onClose}>{t('actions.annuler')}</Button>
          <Button type="primary" loading={loading}
            style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}
            onClick={handleSubmit}>
            {t('emettre.emettre')}
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" size="small">

        {/* ── Souscripteur ── */}
        <Divider orientation="left" style={{ fontSize: 13, color: '#1a6b3c', marginTop: 8 }}>
          <UserOutlined /> {t('emettre.souscripteur')}
        </Divider>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item name="souscripteurNom" label={t('emettre.nom')} rules={[{ required: true, message: t('emettre.requis') }]} style={f}>
              <Input placeholder={t('emettre.nomPlaceholder')} style={{ textTransform: 'uppercase' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="souscripteurPrenom" label={t('emettre.prenoms')} style={f}>
              <Input placeholder={t('emettre.prenoms')} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="souscripteurTelephone" label={t('emettre.telephone')} style={f}>
              <Input placeholder="+228 XX XX XX XX" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="souscripteurEmail" label="Email" style={f}>
              <Input type="email" placeholder={t('emettre.emailExemple')} />
            </Form.Item>
          </Col>
        </Row>

        {/* ── Véhicule ── */}
        <Divider orientation="left" style={{ fontSize: 13, color: '#1a6b3c' }}>
          <CarOutlined /> {t('emettre.vehicule')}
        </Divider>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item name="vehiculeImmatriculation" label={t('emettre.immatriculation')} rules={[{ required: true, message: t('emettre.requis') }]} style={f}>
              <Input placeholder="TG-XXXX-XX" style={{ textTransform: 'uppercase' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="vehiculeMarque" label={t('emettre.marque')} style={f}>
              <Input placeholder="Toyota, Peugeot..." />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="vehiculeModele" label={t('emettre.modele')} style={f}>
              <Input placeholder="Corolla, 308..." />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item name="vehiculeChassis" label={t('emettre.chassis')} style={f}>
              <Input placeholder={t('emettre.chassisPlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="vehiculeGenre" label={t('emettre.genre')} style={f}>
              <Select placeholder={t('emettre.genre')} allowClear>
                <Option value="VP">{t('emettre.genres.VP')}</Option>
                <Option value="VU">{t('emettre.genres.VU')}</Option>
                <Option value="TCO">{t('emettre.genres.TCO')}</Option>
                <Option value="MOTO">{t('emettre.genres.MOTO')}</Option>
                <Option value="CAMION">{t('emettre.genres.CAMION')}</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* ── Police & Contrat ── */}
        <Divider orientation="left" style={{ fontSize: 13, color: '#1a6b3c' }}>
          <FileTextOutlined /> {t('emettre.policeContrat')}
        </Divider>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="compagnieId" label={t('emettre.compagnie')} rules={[{ required: true, message: t('emettre.requis') }]} style={f}>
              <Select placeholder={t('emettre.selectionnerCompagnie')} showSearch optionFilterProp="children">
                {compagnies.map((c) => (
                  <Option key={c.organismeTrackingId} value={c.organismeTrackingId}>
                    {c.raisonSociale}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="numeroPolice" label={t('emettre.numeroPolice')} rules={[{ required: true, message: t('emettre.requis') }]} style={f}>
              <Input placeholder={t('emettre.numeroPolicePlaceholder')} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="encaissementRefExterne" label={t('emettre.refEncaissement')}
              rules={[{ required: true, message: t('emettre.requis') }]}
              tooltip={t('emettre.refEncaissementAide')} style={f}>
              <Input placeholder={t('emettre.refEncaissementExemple')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="typeAttestation" label={t('emettre.typeAttestation')} rules={[{ required: true }]} style={f}>
              <Select>
                <Option value="DIGITALE">{t('emettre.types.DIGITALE')}</Option>
                <Option value="PHYSIQUE">{t('emettre.types.PHYSIQUE')}</Option>
                <Option value="HYBRIDE">{t('emettre.types.HYBRIDE')}</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item name="dateEffet" label={t('emettre.dateEffet')} rules={[{ required: true, message: t('emettre.requis') }]} style={f}>
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="dateEcheance" label={t('emettre.dateEcheance')} rules={[{ required: true, message: t('emettre.requis') }]} style={f}>
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
                disabledDate={(d) => {
                  const effet = form.getFieldValue('dateEffet');
                  return effet && d && d.isBefore(effet, 'day');
                }} />
            </Form.Item>
          </Col>
        </Row>

        {/* ── Couverture ── */}
        <Divider orientation="left" style={{ fontSize: 13, color: '#1a6b3c' }}>
          <GlobalOutlined /> {t('emettre.couverture')}
        </Divider>
        <Row gutter={12}>
          <Col span={6}>
            <Form.Item name="paysEmetteurCode" label={t('emettre.paysEmetteur')} rules={[{ required: true }]} style={f}>
              <Select>
                {PAYS_CEDEAO.map((p) => (
                  <Option key={p.code2} value={p.code2}>{p.flag} {p.nom} ({p.code2})</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={18}>
            <Form.Item name="paysCouverts" label={t('emettre.paysCouverts')} tooltip={t('emettre.paysCouvertsAide')} style={f}>
              <Select mode="multiple" placeholder={t('emettre.selectionnerPays')} maxTagCount={5}>
                {PAYS_CEDEAO.map((p) => (
                  <Option key={p.code2} value={p.code2}>{p.flag} {p.nom}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* ── Canaux d'envoi ── */}
        <Divider orientation="left" style={{ fontSize: 13, color: '#1a6b3c' }}>
          {t('emettre.canaux')}
        </Divider>
        <Form.Item name="canauxEnvoi" style={f}>
          <Checkbox.Group>
            <Space>
              <Checkbox value="EMAIL">Email</Checkbox>
              <Checkbox value="SMS">SMS</Checkbox>
              <Checkbox value="WHATSAPP">WhatsApp</Checkbox>
            </Space>
          </Checkbox.Group>
        </Form.Item>

      </Form>
    </Modal>
  );
}
